<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Can I get a full backup of this ledger, and can I restore one into a fresh ledger?"
  Answer:   the export section mirrors /exports (a job's status carries its own outcome via
            StatusTag, never colour alone); the restore section's outcome is the new ledger's
            name plus the row counts BackupRestoreResult returns, or the server's own rejection
            detail - never a generic failure, since restore validates before writing anything.
  Context:  the restore section explains up front that the target must be a brand-new, empty
            ledger - this screen creates it, the user never points restore at an existing one.
  Controls: "Request backup" and "Restore backup" are each the one --color-action control in
            their own section; the export section is hidden for a role without the journal's
            own view capability, per AGENTS.md's "a control whose capability is absent is not
            rendered, not disabled" - restore needs no capability of its own, since it always
            creates and owns a brand-new ledger.
-->
<script setup lang="ts">
import { plusDays, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  CreateExportForm,
  type CreateExportInput,
  ExportList,
  type ExportJob,
  RestoreBackupForm,
  type RestoreBackupInput,
  canRestoreBackup,
  canViewExports,
  problemDetail,
  useBackupRestore,
  useExports,
} from '@/features/exports'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()
const router = useRouter()

const canExport = computed(() => canViewExports(session.can))
const canRestore = computed(() => canRestoreBackup(session.principal.value !== undefined))

const exports = useExports({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canExport.value,
  format: 'BACKUP',
})

const today = todayUtc(systemClock)
// A backup is the whole ledger, not a reporting window - default to a range wide enough to
// cover any ledger's history rather than echo the CSV screen's last-29-days default.
const defaultFrom = plusDays(today, -3650)

function onCreate(input: CreateExportInput): void {
  exports.create(input)
}

function onDownload(job: ExportJob): void {
  exports.download(job)
}

const restore = useBackupRestore()

function onRestore(input: RestoreBackupInput): void {
  restore.restore(input)
}

const restoreErrorDetail = computed(() => {
  const error = restore.error.value
  if (error === null) return undefined
  return problemDetail(error) ?? t('backupRestore.restoreError')
})

async function openRestoredLedger(): Promise<void> {
  const ledgerId = restore.outcome.value?.ledgerId
  if (ledgerId === undefined) return
  session.selectLedger(ledgerId)
  await router.push({ name: 'transactions' })
}
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-8 p-[var(--space-page)]">
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('backupRestore.title') }}
    </h1>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <template v-else>
      <p v-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
        {{ t('backupRestore.noLedger') }}
      </p>

      <section v-else-if="canExport" class="flex flex-col gap-[var(--space-group)]">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('backupRestore.exportTitle') }}
        </h2>
        <p class="text-[color:var(--color-text-muted)]">{{ t('backupRestore.exportBody') }}</p>

        <CreateExportForm
          format="BACKUP"
          :default-from="defaultFrom"
          :default-until="today"
          :is-creating="exports.isCreating.value"
          :error="exports.createError.value"
          @submit="onCreate"
        />

        <ExportList
          :jobs="exports.jobs.value"
          :is-loading="exports.isLoading.value"
          :error="exports.error.value"
          :retrying="exports.retrying.value"
          :downloading-id="exports.downloadingId.value"
          :empty-title="t('backupRestore.emptyTitle')"
          :empty-body="t('backupRestore.emptyBody')"
          @retry="exports.retry()"
          @download="onDownload"
        />
      </section>

      <section v-if="canRestore" class="flex flex-col gap-[var(--space-group)]">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('backupRestore.restoreTitle') }}
        </h2>
        <p class="text-[color:var(--color-text-muted)]">{{ t('backupRestore.restoreBody') }}</p>

        <RestoreBackupForm
          :is-restoring="restore.isRestoring.value"
          :error-detail="restoreErrorDetail"
          @submit="onRestore"
        />

        <div
          v-if="restore.outcome.value"
          role="status"
          class="flex flex-col items-start gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
        >
          <p class="text-[color:var(--color-text)]">
            {{
              t('backupRestore.restoreSuccess', {
                accounts: restore.outcome.value.result.accounts,
                entries: restore.outcome.value.result.entries,
              })
            }}
          </p>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
            @click="openRestoredLedger"
          >
            {{ t('backupRestore.openRestoredLedger') }}
          </button>
        </div>
      </section>
    </template>
  </main>
</template>
